import * as React from "react";
import * as locales from "../../../../locales.json";
import styled from "styled-components";
import { I18n } from "i18n-js";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

import EditChargebeeInfoIcon from "../../../reusable/editChargebeeInfoIcon";
import {
  CreditCardTypes,
  CreditCardPaymentMethodPropTypes,
  BillingType,
} from "../lib/types";
import {
  StyledHelperText,
  StyledParagraphBold,
  StyledParagraph,
  StyledLinkSpan,
} from "../lib/styles";
import { theme } from "../../../theme/theme";
import Button from "../../../reusable/button";

const amex = require("../../../svg/amex.svg");
const bancontact = require("../../../svg/bancontact.svg");
const dinersClub = require("../../../svg/dinersClub.svg");
const discover = require("../../../svg/discover.svg");
const jcb = require("../../../svg/jcb.svg");
const mastercard = require("../../../svg/mastercard.svg");
const visa = require("../../../svg/visa.svg");

const StyledCardNumExpContainer = styled.div`
  display: inline-block;
`;

const StyledContainer = styled.div`
  align-items: center;
  display: flex;
`;

const StyledCCImage = styled.img`
  height: 36px;
  margin-right: 16px;
  border: 1px solid ${theme.color.grayLightest};
  border-radius: 4px;
`;

const StyledCCIcon = styled.i`
  font-size: 36px;
  margin-right: 16px;
  color: ${theme.color.grayDarkest};
`;

const StyledHelperWrapper = styled.div`
  margin-top: 8px;
`;

const StyledSpan = styled(StyledLinkSpan)`
  display: block;
  margin-bottom: 16px;
  padding-right: 40px;
`;

export const getCardDisplayName = (cardType: CreditCardTypes): string => {
  switch (cardType) {
    case "american_express":
      return "American Express";
    case "bancontact":
      return "Bancontact";
    case "diners_club":
      return "Diners Club";
    case "discover":
      return "Discover";
    case "jcb":
      return "JCB";
    case "mastercard":
      return "Mastercard";
    case "not_applicable":
      return "N/A";
    case "visa":
      return "Visa";
    case "other":
    default:
      return i18n.translate("admin_pages.settings.billing.other");
  }
};

const getCardImage = (cardType: CreditCardTypes): React.ReactElement => {
  switch (cardType) {
    case "american_express":
      return <StyledCCImage src={amex} alt={getCardDisplayName(cardType)} />;
    case "bancontact":
      return (
        <StyledCCImage src={bancontact} alt={getCardDisplayName(cardType)} />
      );
    case "diners_club":
      return (
        <StyledCCImage src={dinersClub} alt={getCardDisplayName(cardType)} />
      );
    case "discover":
      return (
        <StyledCCImage src={discover} alt={getCardDisplayName(cardType)} />
      );
    case "jcb":
      return <StyledCCImage src={jcb} alt={getCardDisplayName(cardType)} />;
    case "mastercard":
      return (
        <StyledCCImage src={mastercard} alt={getCardDisplayName(cardType)} />
      );
    case "visa":
      return <StyledCCImage src={visa} alt={getCardDisplayName(cardType)} />;
    case "not_applicable":
      return <></>;
    default:
      return (
        <StyledCCIcon
          className="fas fa-credit-card-front"
          data-testid="cc-icon"
        />
      );
  }
};

const CreditCardPaymentMethod = ({
  cardType,
  expiration,
  last4,
  openPortal,
  rewardBillingType,
  setRefetchData,
  subscriptionAutoCollection,
}: CreditCardPaymentMethodPropTypes): React.ReactElement => {
  const subscriptionUsesCC = subscriptionAutoCollection === "on";
  const rewardsUsesCC =
    rewardBillingType === BillingType.PayAsYouGo ||
    rewardBillingType === BillingType.Flex;
  const subscriptionAndRewardsUseCC = subscriptionUsesCC && rewardsUsesCC;

  return (
    <div data-testid="cc-payment-method">
      {cardType ? (
        <>
          <EditChargebeeInfoIcon
            altText={i18n.translate(
              "admin_pages.settings.billing.edit_payment_method"
            )}
            amplitudeEventName="Billing Settings: Payment Method"
            closeModalCallback={(): void => {
              return setRefetchData(true);
            }}
            openPortal={openPortal}
            portalSection="PAYMENT_SOURCES"
            testId="payment-method-edit-icon"
          />

          <StyledContainer>
            {getCardImage(cardType)}
            <StyledCardNumExpContainer>
              <StyledParagraphBold data-testid="card-last4">
                {`${getCardDisplayName(cardType)} ending in ${last4}`}
              </StyledParagraphBold>
              <StyledParagraph data-testid="expiration">
                {`${i18n.translate(
                  "admin_pages.settings.billing.expires"
                )} ${expiration}`}
              </StyledParagraph>
            </StyledCardNumExpContainer>
          </StyledContainer>
          {subscriptionAndRewardsUseCC && (
            <StyledHelperWrapper data-testid="same-cc-message">
              <StyledHelperText>
                {i18n.translate(
                  "admin_pages.settings.billing.same_credit_card"
                )}
              </StyledHelperText>
            </StyledHelperWrapper>
          )}
        </>
      ) : (
        <>
          <StyledSpan>
            {i18n.translate(
              "admin_pages.settings.billing.add_credit_card_description"
            )}
          </StyledSpan>
          <Button
            onClick={(event): void => {
              window.amplitude.track("Add Credit Card Modal Opened");
              return openPortal(event, "PAYMENT_SOURCES", () => {
                return setRefetchData(true);
              });
            }}
            size="small"
            testId="add-credit-card-button"
            variant="primary"
          >
            {i18n.translate("admin_pages.settings.billing.add_credit_card")}
          </Button>
        </>
      )}
    </div>
  );
};

export default CreditCardPaymentMethod;
